import React, { useEffect, useState } from "react";
import api from "../../api";

const ClosedTickets = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [error, setError] = useState("");

  const fetchClosedConversations = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/admin/support/conversations/closed");
      const items = res.data.conversations || [];
      setConversations(items);
      if (items.length > 0) fetchConversationDetails(items[0].id);
      else setSelectedConversation(null);
    } catch (err) {
      setError("Erreur lors du chargement de l'historique des tickets.");
    } finally {
      setLoading(false);
    }
  };

  const fetchConversationDetails = async (conversationId) => {
    try {
      setError("");
      const res = await api.get(`/admin/support/conversations/${conversationId}`);
      setSelectedConversation(res.data.conversation);
    } catch (err) {
      setError("Erreur lors du chargement du ticket.");
    }
  };

  useEffect(() => { fetchClosedConversations(); }, []);

  const handleReopenTicket = async () => {
    if (!selectedConversation) return;
    try {
      setUpdatingStatus(true);
      await api.patch(`/admin/support/conversations/${selectedConversation.id}/status`, { status: "open" });
      const remaining = conversations.filter((c) => c.id !== selectedConversation.id);
      setConversations(remaining);
      if (remaining.length > 0) fetchConversationDetails(remaining[0].id);
      else setSelectedConversation(null);
    } catch (err) {
      setError("Erreur lors de la réouverture du ticket.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-6">
        <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
          <p className="text-slate-300">Chargement des tickets fermés...</p>
        </div>
      </main>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-6">
        {error && (
          <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
        )}

        <section className="grid min-h-[720px] grid-cols-1 gap-6 xl:grid-cols-12">
          <div className="xl:col-span-4">
            <div className="h-full overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-sm">
              <div className="border-b border-slate-800 px-5 py-4">
                <h2 className="text-lg font-semibold text-white">Tickets fermés</h2>
              </div>
              <div className="max-h-[620px] overflow-y-auto">
                {conversations.length > 0 ? conversations.map((conversation) => (
                  <button key={conversation.id}
                    onClick={() => fetchConversationDetails(conversation.id)}
                    className={`w-full border-b border-slate-800 px-5 py-4 text-left transition ${
                      selectedConversation?.id === conversation.id ? "bg-slate-800/80" : "hover:bg-slate-800/50"
                    }`}>
                    <p className="truncate font-semibold text-slate-100">
                      {conversation.user?.firstname} {conversation.user?.lastname}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">Conversation #{conversation.id}</p>
                  </button>
                )) : (
                  <div className="px-5 py-6 text-sm text-slate-400">Aucun ticket fermé trouvé.</div>
                )}
              </div>
            </div>
          </div>

          <div className="xl:col-span-8">
            <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-sm">
              {selectedConversation ? (
                <>
                  <div className="border-b border-slate-800 px-6 py-5 flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-white">
                      {selectedConversation.user?.firstname} {selectedConversation.user?.lastname}
                    </h2>
                    <button onClick={handleReopenTicket} disabled={updatingStatus}
                      className="rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-600 disabled:opacity-60">
                      {updatingStatus ? "Mise à jour..." : "Rouvrir le ticket"}
                    </button>
                  </div>

                  <div className="flex-1 space-y-4 overflow-y-auto bg-slate-950/60 px-6 py-6">
                    {selectedConversation.messages?.map((msg) => (
                      <div key={msg.id} className={`flex ${msg.senderRole === "admin" ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm ${
                          msg.senderRole === "admin"
                            ? "bg-cyan-600 text-white"
                            : "border border-slate-700 bg-slate-800 text-slate-100"
                        }`}>
                          <p className="mb-1 text-xs font-semibold opacity-80">
                            {msg.senderRole === "admin" ? "Admin" : `${msg.sender?.firstname || ""} ${msg.sender?.lastname || ""}`}
                          </p>
                          <p className="whitespace-pre-wrap break-words leading-6">{msg.message}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex flex-1 items-center justify-center text-slate-400">
                  Sélectionnez un ticket fermé pour afficher son historique.
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default ClosedTickets;